import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { InvertedPanel } from "../../components/BoxView/InvertedPanel";
import { OrderLineList } from "../order-details/subcomponents/OrderLineList";
import { SecondaryButton } from "../../components/Buttons/SecondaryButton";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { PriceTotal } from "../order-details/subcomponents/PriceTotal";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { BoxFooter } from "../../components/BoxView/BoxFooter";
import { OrderForm } from "./subcomponents/OrderDeliveryForm";
import {
  DeliveryTypeOption,
  OrderDeliveryForm,
  PackagingOption,
} from "./useOrderConfirmationEditViewModel";
import {
  DeliveryType,
  Order,
  OrderLine,
  Packaging,
} from "../../../core/models/entities/Order";
import { LoadingView } from "../../components/loading-view/LoadingView";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { Role } from "../../../core/models/entities/Role";
import { Switch } from "../../components/ui/Switch";
import { OrderCo2eResult } from "../../../utils/orderCo2e";
import { OrderCo2eEstimate } from "./subcomponents/OrderCo2eEstimate";
import { useWorkshopMode } from "../../../hooks/useWorkshopMode";
import {
  AwaitingPaymentNotice,
  PayNowButton,
} from "../../components/Order/PayNow";

export interface OrderDeliveryFormViewModel {
  order: Order;
  orderNo: string;
  orderlines: OrderLine[];
  handleBack: () => void;
  handleChange: () => void;
  handleConfirm: () => void;
  form: OrderDeliveryForm;
  deliveryTypeGroupList: DeliveryTypeOption[];
  packagingGroupList: PackagingOption[];
  handleOrderFormUpdate: (form: OrderDeliveryForm) => void;
  handlePackagingOrDeliveryChange?: (
    deliveryType: DeliveryType,
    packaging: Packaging
  ) => void;
  subTotal: number;
  vat: number;
  totalAmount: number;
  isLoading: boolean;
  readonly?: boolean;
  isFormValid: boolean;
  validationErrors: string[];
  showValidation: boolean;
  showCustomerPrices: boolean;
  setShowCustomerPrices: (value: boolean) => void;
  co2eEstimate?: OrderCo2eResult | null;
}

export function OrderConfirmationComponent({
  order,
  orderlines,
  orderNo,
  form,
  isLoading,
  handleOrderFormUpdate,
  handlePackagingOrDeliveryChange,
  handleBack,
  handleChange,
  handleConfirm,
  deliveryTypeGroupList,
  packagingGroupList,
  subTotal,
  vat,
  totalAmount,
  readonly,
  isFormValid,
  validationErrors,
  showValidation,
  showCustomerPrices,
  setShowCustomerPrices,
  co2eEstimate,
}: OrderDeliveryFormViewModel) {
  const { t } = useTranslation();
  const { currency, role, organizationId } = useProfileContext();
  // Workshop mode: flagged users cannot reach the cart flow (CartRoute blocks
  // it), but gate the price surfaces anyway - fail closed.
  const workshopMode = useWorkshopMode();
  useEffect(() => {
    if (order.deliveryType == null || order.packagingType == null)
      handlePackagingOrDeliveryChange?.call(
        null,
        form.deliveryType,
        form.packaging
      );
  }, []);

  if (isLoading) return <LoadingView />;

  return (
    <JetSportBox className={"min-h-[900px]"}>
      <BoxHeader2 className={"text-sm flex justify-between items-center"}>
        <p className="uppercase">
          {readonly ? (
            <span>
              <b> {t("order")}</b> - #{orderNo}
            </span>
          ) : (
            <span className="font-bold">
              {t("orderConfirmation.confirmOrder")}
            </span>
          )}
        </p>
        {role === Role.Vendor &&
          !workshopMode &&
          readonly &&
          order.organization.id !== organizationId && (
            <div className={"flex items-center gap-x-2"}>
              <label className={"text-sm font-normal"}>
                {t("orderConfirmation.showCustomerPrices")}
              </label>
              <Switch
                checked={showCustomerPrices}
                onCheckedChange={setShowCustomerPrices}
              />
            </div>
          )}
      </BoxHeader2>
      <BoxContent>
        {/* Stripe prepayment: vendors land on this read-only view from the
            order list, so surface the parked-for-payment state here too */}
        <AwaitingPaymentNotice order={order} className={"mt-4"} />
        <div className={"flex-1 flex-col my-10"}>
          <OrderLineList
            hideColumns={
              workshopMode
                ? { unitPrice: true, price: true, subTotal: true }
                : undefined
            }
            orderlines={orderlines}
            currency={order.currency}
            showCustomerPrices={showCustomerPrices}
            readonly={readonly}
          />
          {!workshopMode && (
            <PriceTotal
              subTotal={subTotal}
              vat={vat}
              totalAmount={totalAmount}
              currency={order.currency ?? currency}
            />
          )}
          {co2eEstimate && (
            <div className={"flex justify-end mt-3 pr-4"}>
              <OrderCo2eEstimate
                total={co2eEstimate.total}
                perLogo={co2eEstimate.perLogo}
              />
            </div>
          )}
        </div>
        <OrderForm
          form={form}
          order={order}
          readonly={readonly}
          onFormChange={handleOrderFormUpdate}
          onDeliveryOrPackagingChange={handlePackagingOrDeliveryChange}
          deliveryTypeOptionList={deliveryTypeGroupList}
          packagingOptionList={packagingGroupList}
          validationErrors={validationErrors}
          showValidation={showValidation}
        />
      </BoxContent>
      <div className={"flex justify-between ml-8 mr-10 mt-4"}>
        <SecondaryButton onClick={handleBack}>{t("back")}</SecondaryButton>
        <div className={"flex justify-between gap-x-8"}>
          {!readonly && (
            <>
              <SecondaryButton onClick={handleChange}>
                {t("orderConfirmation.change")}
              </SecondaryButton>
              <PrimaryButton
                onClick={handleConfirm}
                disabled={form.isFetchingDate}
              >
                {t("orderConfirmation.placeOrder")}
              </PrimaryButton>
            </>
          )}
          {/* Self-gating: only renders while the order awaits Stripe payment */}
          {readonly && <PayNowButton order={order} />}
        </div>
      </div>
      <BoxFooter className={"justify-between"}></BoxFooter>
    </JetSportBox>
  );
}
